import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { calculateStrip } from '../services/stripCalculations';
import { normalizeNumericInput, toNumber } from '../utils/numericInput';

const ROLL_WIDTH_OPTIONS = [160, 152, 137, 127, 11, 1, 62, 61, 60];
const CUT_THICKNESS_OPTIONS = [0.1, 0.2, 0.3, 0.4, 0.5];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value || 0);
};

/**
 * CalculationModal
 * Computes unit_price (and, for "tira", area/quantity too) from a
 * calculation type for a Client Quote row.
 */
const CalculationModal = ({ isOpen, onClose, onApply, item, product }) => {
  const { t } = useTranslation();

  // Every numeric field holds the raw (normalised) string the user typed, not a
  // parsed number, so it can be cleared and never accumulates a stale leading
  // zero. Parsing happens where the value is used, never on the way in.
  const [calculationType, setCalculationType] = useState(item?.calculation_type || 'unidad');
  const [width, setWidth] = useState(normalizeNumericInput(item?.width ?? ''));
  const [height, setHeight] = useState(normalizeNumericInput(item?.height ?? ''));

  const [rollWidth, setRollWidth] = useState(item?.roll_width ?? ROLL_WIDTH_OPTIONS[0]);
  const [cutMargin, setCutMargin] = useState(normalizeNumericInput(item?.cut_margin ?? 4));
  const [cutThickness, setCutThickness] = useState(item?.cut_thickness ?? CUT_THICKNESS_OPTIONS[0]);
  const [chargeCuts, setChargeCuts] = useState(item?.charge_cuts ?? false);
  const [pricePerCut, setPricePerCut] = useState(
    normalizeNumericInput(item?.price_per_cut ?? 0.5)
  );
  const [pieces, setPieces] = useState(normalizeNumericInput(item?.pieces ?? ''));

  const handleNumericChange = (setter) => (e) => setter(normalizeNumericInput(e.target.value));

  const basePrice = toNumber(product?.price);
  const widthValue = toNumber(width);
  const heightValue = toNumber(height);

  const previewPrice = calculationType === 'm2'
    ? widthValue * heightValue * basePrice
    : basePrice;

  const strip = calculationType === 'tira'
    ? calculateStrip({
        rollWidth, cutMargin, cutThickness, width: widthValue, height: heightValue, pieces,
        productPrice: basePrice, chargeCuts, pricePerCut
      })
    : null;

  const handleApply = () => {
    if (calculationType === 'unidad') {
      onApply({ calculation_type: 'unidad', width: null, height: null, unit_price: basePrice, area: 'N/A' });
    } else if (calculationType === 'm2') {
      onApply({
        calculation_type: 'm2',
        width: widthValue,
        height: heightValue,
        unit_price: widthValue * heightValue * basePrice,
        area: `${widthValue} X ${heightValue} M`
      });
    } else {
      onApply({
        calculation_type: 'tira',
        roll_width: toNumber(rollWidth),
        cut_margin: toNumber(cutMargin),
        cut_thickness: toNumber(cutThickness),
        charge_cuts: chargeCuts,
        price_per_cut: toNumber(pricePerCut),
        pieces: toNumber(pieces),
        width: widthValue,
        height: heightValue,
        cm2: strip.cm2,
        width_adjustment: strip.widthAdjustment,
        height_adjustment: strip.heightAdjustment,
        waste_percentage: strip.wastePercentage,
        waste_percentage_height: strip.wastePercentageByHeight,
        is_orientation_optimized: strip.betterByHeight,
        pieces_per_strip: strip.piecesPerStrip,
        strips_quantity: strip.stripsQuantity,
        cuts_quantity: strip.cutsQuantity,
        strip_area: strip.stripArea,
        strip_price: strip.stripPrice,
        price_per_piece: strip.pricePerPiece,
        unit_price: strip.pricePerPiece,
        area: `${widthValue} X ${heightValue} CM`,
        quantity: toNumber(pieces)
      });
    }
    onClose();
  };

  const dimensionInputClass = (calculationType === 'tira' && strip?.betterByHeight)
    ? 'form-input error'
    : 'form-input';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('clientQuotes.calculationModalTitle')}>
      <div className="form-group">
        <label className="form-label" htmlFor="calculation-type-select">
          {t('clientQuotes.calculationTypeLabel')}
        </label>
        <select
          id="calculation-type-select"
          value={calculationType}
          onChange={(e) => setCalculationType(e.target.value)}
          className="form-select"
        >
          <option value="unidad">{t('clientQuotes.calculationTypeUnit')}</option>
          <option value="m2">{t('clientQuotes.calculationTypeM2')}</option>
          <option value="tira">{t('clientQuotes.calculationTypeStrip')}</option>
        </select>
      </div>

      {(calculationType === 'm2' || calculationType === 'tira') && (
        <div className="grid-2">
          <div className="form-group">
            <label className="form-label" htmlFor="calculation-width-input">
              {t('clientQuotes.width')}
            </label>
            <input
              id="calculation-width-input"
              type="text"
              inputMode="decimal"
              value={width}
              onChange={handleNumericChange(setWidth)}
              className={calculationType === 'tira' ? dimensionInputClass : 'form-input'}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="calculation-height-input">
              {t('clientQuotes.height')}
            </label>
            <input
              id="calculation-height-input"
              type="text"
              inputMode="decimal"
              value={height}
              onChange={handleNumericChange(setHeight)}
              className={calculationType === 'tira' ? dimensionInputClass : 'form-input'}
            />
          </div>
        </div>
      )}

      {calculationType === 'tira' && strip?.betterByHeight && (
        <p className="form-error" role="alert">
          {t('clientQuotes.rotateSuggestion')}
        </p>
      )}

      {calculationType === 'tira' && (
        <>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label" htmlFor="calculation-roll-width-select">
                {t('clientQuotes.rollWidth')}
              </label>
              <select
                id="calculation-roll-width-select"
                value={rollWidth}
                onChange={(e) => setRollWidth(e.target.value)}
                className="form-select"
              >
                {ROLL_WIDTH_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="calculation-cut-margin-input">
                {t('clientQuotes.cutMargin')}
              </label>
              <input
                id="calculation-cut-margin-input"
                type="text"
                inputMode="decimal"
                value={cutMargin}
                onChange={handleNumericChange(setCutMargin)}
                className="form-input"
              />
            </div>
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label className="form-label" htmlFor="calculation-cut-thickness-select">
                {t('clientQuotes.cutThickness')}
              </label>
              <select
                id="calculation-cut-thickness-select"
                value={cutThickness}
                onChange={(e) => setCutThickness(e.target.value)}
                className="form-select"
              >
                {CUT_THICKNESS_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="calculation-pieces-input">
                {t('clientQuotes.pieces')}
              </label>
              <input
                id="calculation-pieces-input"
                type="text"
                inputMode="decimal"
                value={pieces}
                onChange={handleNumericChange(setPieces)}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="calculation-charge-cuts-checkbox">
              <input
                id="calculation-charge-cuts-checkbox"
                type="checkbox"
                checked={chargeCuts}
                onChange={(e) => setChargeCuts(e.target.checked)}
              />
              {' '}{t('clientQuotes.chargeCuts')}
            </label>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="calculation-price-per-cut-input">
              {t('clientQuotes.pricePerCut')}
            </label>
            <input
              id="calculation-price-per-cut-input"
              type="text"
              inputMode="decimal"
              value={pricePerCut}
              onChange={handleNumericChange(setPricePerCut)}
              className="form-input"
            />
          </div>

          {strip && (
            <div className="mb-20">
              <div className="form-group">
                <span className="text-bold">{t('clientQuotes.cm2')}: </span>
                <span>{strip.cm2.toFixed(2)}</span>
              </div>
              <div className="form-group">
                <span className="text-bold">{t('clientQuotes.widthAdjustment')}: </span>
                <span>{strip.widthAdjustment.toFixed(2)}</span>
              </div>
              <div className="form-group">
                <span className="text-bold">{t('clientQuotes.wastePercentage')}: </span>
                <span>{strip.wastePercentage.toFixed(2)}%</span>
              </div>
              <div className="form-group">
                <span className="text-bold">{t('clientQuotes.piecesPerStrip')}: </span>
                <span>{strip.piecesPerStrip}</span>
              </div>
              <div className="form-group">
                <span className="text-bold">{t('clientQuotes.stripsQuantity')}: </span>
                <span>{strip.stripsQuantity}</span>
              </div>
              <div className="form-group">
                <span className="text-bold">{t('clientQuotes.cutsQuantity')}: </span>
                <span>{strip.cutsQuantity}</span>
              </div>
              <div className="form-group">
                <span className="text-bold">{t('clientQuotes.stripPrice')}: </span>
                <span>{formatCurrency(strip.stripPrice)}</span>
              </div>
              <div className="form-group">
                <span className="text-bold">{t('clientQuotes.pricePerPiece')}: </span>
                <span>{formatCurrency(strip.pricePerPiece)}</span>
              </div>
            </div>
          )}
        </>
      )}

      {calculationType !== 'tira' && (
        <div className="form-group">
          <span className="text-bold">{t('clientQuotes.unitPrice')}: </span>
          <span className="text-bold">{formatCurrency(previewPrice)}</span>
        </div>
      )}

      <div className="form-actions">
        <button type="button" onClick={handleApply} className="btn btn-primary">
          {t('clientQuotes.apply')}
        </button>
        <button type="button" onClick={onClose} className="btn btn-secondary">
          {t('common.cancel')}
        </button>
      </div>
    </Modal>
  );
};

export default CalculationModal;
